import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface CapacityImpactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tableName: string;
  oldCapacity: number;
  newCapacity: number;
  exceededReservations: Array<{
    id: string;
    date: string;
    startTime: string;
    numberOfPeople: number;
    customerName: string;
  }>;
  onConfirm: () => void;
}

export function CapacityImpactDialog({
  open,
  onOpenChange,
  tableName,
  oldCapacity,
  newCapacity,
  exceededReservations,
  onConfirm,
}: CapacityImpactDialogProps) {
  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>⚠️ Modification de capacité</AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            <div className="text-base">
              <strong>{tableName}</strong>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-muted-foreground">Ancienne capacité :</span>
                <span className="font-semibold">{oldCapacity} places</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Nouvelle capacité :</span>
                <span className="font-semibold">{newCapacity} places</span>
              </div>
            </div>

            {exceededReservations.length > 0 && (
              <div className="bg-destructive/10 p-3 rounded-md border border-destructive/20">
                <p className="font-medium text-destructive mb-2">
                  ⚠️ {exceededReservations.length} réservation(s) future(s) dépassent la nouvelle capacité :
                </p>
                <ul className="space-y-1 text-sm">
                  {exceededReservations.slice(0, 5).map(res => (
                    <li key={res.id} className="flex items-center justify-between">
                      <span>
                        {format(new Date(res.date), "d MMM", { locale: fr })} à {res.startTime.slice(0, 5)}
                      </span>
                      <span className="font-medium">
                        {res.numberOfPeople} personnes - {res.customerName}
                      </span>
                    </li>
                  ))}
                  {exceededReservations.length > 5 && (
                    <li className="text-muted-foreground italic">
                      ... et {exceededReservations.length - 5} autre(s)
                    </li>
                  )}
                </ul>
              </div>
            )}

            <p className="text-sm text-muted-foreground">
              Les réservations existantes ne seront pas automatiquement modifiées.
              Vous devrez gérer manuellement ces réservations si nécessaire.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Annuler</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm}>
            Modifier quand même
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
